import { AlertCircle, CloudOff, RefreshCw } from 'lucide-react'
import { isOfflineError } from '../lib/offline'

/**
 * Standard error state for a failed data query.
 *
 * When the failure is just a lack of connectivity it shows a calm "you're
 * offline" card so offline reads feel intentional rather than broken. A real
 * error (a 4xx/5xx that carried a server response) renders the usual red error
 * with the server message. Pass `onRetry` for a Retry button and/or `children`
 * for a custom action (e.g. a back link).
 *
 *   <DataLoadError error={error} title="Failed to load dashboard" onRetry={refetch} />
 *   <DataLoadError error={error} title="Failed to load animal">
 *     <Link to="/cattle" className="btn-primary mt-4 inline-block">Back to Cattle List</Link>
 *   </DataLoadError>
 */
export default function DataLoadError({ error, title = 'Failed to load', onRetry, children }) {
  if (isOfflineError(error)) {
    return (
      <div className="card p-6 text-center">
        <CloudOff className="h-12 w-12 text-amber-500 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900">You&apos;re offline</h3>
        <p className="text-gray-500 mt-1">
          Showing what&apos;s saved on your device. This will refresh automatically when you reconnect.
        </p>
        {onRetry && (
          <button onClick={onRetry} className="btn-primary mt-4">
            <RefreshCw className="h-4 w-4 mr-2" />
            Try again
          </button>
        )}
        {children}
      </div>
    )
  }

  return (
    <div className="card p-6 text-center">
      <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
      <h3 className="text-lg font-medium text-gray-900">{title}</h3>
      <p className="text-gray-500 mt-1">{error?.message || 'Something went wrong.'}</p>
      {onRetry && (
        <button onClick={onRetry} className="btn-primary mt-4">
          <RefreshCw className="h-4 w-4 mr-2" />
          Retry
        </button>
      )}
      {children}
    </div>
  )
}
